import { useEffect, useRef, useState } from 'react';
import { scheduleRequest } from '../lib/requestScheduler';
import { isDocumentVisible, onVisibilityChange } from '../lib/visibility';

export interface PollingState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** Tijdstip (ms, Date.now()) van de laatste GESLAAGDE fetch, of null zolang
   *  er nog geen is. Bij een fout houdt `data` de vorige waarde vast; zonder
   *  dit tijdstip kan een consument niet zien hoe oud die waarde is (#202). */
  lastSuccessAt: number | null;
}

/**
 * Is de data van deze poller niet meer te vertrouwen als actuele stand?
 * Ja bij een fout op de laatste poging (de oude `data` blijft dan staan), en
 * ja als de laatste geslaagde fetch langer dan `maxAgeMs` geleden is. Zolang
 * er nog nooit iets binnenkwam is er niets verouderd: dan is `data` null.
 */
export function isPollingStale<T>(state: PollingState<T>, maxAgeMs: number, now: number = Date.now()): boolean {
  if (state.data == null) return false;
  if (state.error != null) return true;
  return state.lastSuccessAt != null && now - state.lastSuccessAt > maxAgeMs;
}

export interface PollingOptions {
  /** Pause polling when false. Default true. Useful om fetches te blokkeren
   *  tot een gate (auth) gepasseerd is — voorkomt 401-spam in console. */
  enabled?: boolean;
  /** Backoff bij fetch-fout (ms). Default: min(intervalMs, 30s). Voorkomt
   *  dat fetchers met lange intervallen (bv. installation = 1u) na een
   *  transient fout (deploy-restart, netwerk-flap) een uur lang met
   *  `data: null` blijven hangen voor de UI. Bug-rapport Bram 2026-05-14:
   *  na deploy-restart toonde dashboard "x2 · SOC X%" want install-fetch
   *  faalde één keer en de retry was pas over 1u. */
  retryOnErrorMs?: number;
  /** Volgorde van de EERSTE fetch binnen de mount-burst (lager = eerder).
   *  Zie lib/requestScheduler. Alleen de eerste fetch loopt via de
   *  concurrency-limiet; alle vervolg-polls gaan er rechtstreeks doorheen,
   *  zodat `intervalMs` en daarmee de versheid van de data onaangetast
   *  blijft. Weglaten = laagste prioriteit. */
  priority?: number;
  /** Verandert deze waarde, dan wordt er METEEN opnieuw gehaald in plaats van
   *  bij de volgende tick. Voor fetchers met een parameter die tijdens het
   *  laden nog verschuift, zoals het aantal uren historie: dat hangt af van de
   *  prijzen, die bij mount nog niet binnen zijn. Zonder dit bleef de eerste,
   *  te korte historie een vol interval (60 s) op het scherm staan. Bram,
   *  2026-10-17: "Ik zie bij NS en Hooi niet meer de hele historie." */
  refetchKey?: string | number;
}

export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  opts: PollingOptions = {},
): PollingState<T> {
  const enabled = opts.enabled !== false;
  const retryOnErrorMs = opts.retryOnErrorMs ?? Math.min(intervalMs, 30_000);
  const [state, setState] = useState<PollingState<T>>({ data: null, error: null, loading: enabled, lastSuccessAt: null });

  // 2026-05-18 fix: fetcher in een ref houden zodat inline arrow functions
  // (bv. `() => fetchX(20, { transitionsOnly: true })`) niet elke render een
  // nieuwe useEffect-cycle triggeren. Eerder probleem: NS browser pollde 10
  // endpoints; elke binnenkomende setState rerenderde TechPage; nieuwe arrow
  // identity → useEffect cleanup → `cancelled=true` → langzame fetch (zoals
  // NS shadow-commands) kreeg `setState` skipped want cancelled vóór resolve.
  // Resultaat: tegel bleef permanent "Laden...". Met ref: fetcher-identity
  // verandert niet, useEffect blijft mounted, tick.setState gaat altijd door.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Alleen de allereerste fetch van deze hook-instantie loopt via de
  // burst-scheduler. Blijft ook staan als de effect-cycle opnieuw draait
  // (bv. `enabled` flipt), zodat een hervatting niet weer in de wachtrij komt.
  const firstFetchRef = useRef(true);
  const priority = opts.priority;
  const refetchKey = opts.refetchKey;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let lastWasError = false;
    // Staat de keten stil omdat de tab verborgen raakte? Zie lib/visibility:
    // een tab die open blijft staan op een scherm dat niemand ziet pollde
    // anders onbeperkt door (auditbevinding #69). We plannen dan géén
    // vervolg-tick meer en hervatten pas bij terugkeer.
    let pausedHidden = false;

    const tick = async () => {
      const queued = firstFetchRef.current;
      firstFetchRef.current = false;
      try {
        const data = queued
          ? await scheduleRequest(() => fetcherRef.current(), priority)
          : await fetcherRef.current();
        if (!cancelled) setState({ data, error: null, loading: false, lastSuccessAt: Date.now() });
        lastWasError = false;
      } catch (err) {
        if (!cancelled) setState((s) => ({ ...s, error: err as Error, loading: false }));
        lastWasError = true;
      } finally {
        if (!cancelled) {
          if (isDocumentVisible()) {
            const nextDelay = lastWasError ? retryOnErrorMs : intervalMs;
            timer = setTimeout(tick, nextDelay);
          } else {
            pausedHidden = true;
          }
        }
      }
    };

    // Terug in beeld: meteen verversen in plaats van het interval afwachten,
    // anders kijkt de gebruiker naar cijfers van vóór de pauze.
    const resume = () => {
      if (cancelled || !pausedHidden || !isDocumentVisible()) return;
      pausedHidden = false;
      void tick();
    };
    const offVisibility = onVisibilityChange(resume);

    // De eerste fetch draait altijd, ook als de tab bij mount al verborgen is:
    // dan staat de data klaar zodra iemand kijkt, en klopt `loading` meteen.
    tick();
    return () => { cancelled = true; clearTimeout(timer); offVisibility(); };
  }, [intervalMs, enabled, retryOnErrorMs, priority, refetchKey]);

  return state;
}
